/**
 * CoWork task history — the CoWork-mode chat list (hybrid CoWork). Reuses the
 * shared chat history but shows ONLY chats that ran in CoWork (tracked locally
 * in the CoWork store, since there's no server-side tag). Flat, most-recent
 * first, under one clear header. Desktop-only.
 *
 * @module components/cowork/cowork-task-history
 */

import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { DynamicIcon } from "lucide-react/dynamic";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from "@/components/ui/sidebar";
import { useChatHistory } from "@/hooks/use-chat-history";
import { resolveChatIcon } from "@/lib/chat-icon";
import { useCoworkStore } from "@/store/cowork-store";
import { SUPER_AGENT_ID, isSuperAgentId } from "@taqat/core/super-agent";
import type { ChatHistoryApiItem, GroupedChatData } from "@taqat/core";

const GROUP_ORDER: Array<keyof GroupedChatData> = [
  "today",
  "yesterday",
  "lastWeek",
  "lastMonth",
  "older",
];

// Same date-bucket copy as the normal chat sidebar, so CoWork reads identically.
const GROUP_LABEL: Record<keyof GroupedChatData, string> = {
  today: "Today",
  yesterday: "Yesterday",
  lastWeek: "Last 7 days",
  lastMonth: "Last 30 days",
  older: "Older",
};

function chatPath(chat: ChatHistoryApiItem, appId?: string): string {
  const agentIdForUrl =
    chat.agentId && !isSuperAgentId(chat.agentId) ? chat.agentId : SUPER_AGENT_ID;
  return `/app/${appId}/agents/${agentIdForUrl}/chat/${chat.id}`;
}

export function CoworkTaskHistory({ appId }: { appId?: string }) {
  const location = useLocation();
  const coworkChatIds = useCoworkStore((s) => s.coworkChatIds);
  const { groupedData, isLoading } = useChatHistory({ limit: 50, autoFetch: true });

  const idSet = useMemo(() => new Set(coworkChatIds), [coworkChatIds]);
  // Date-bucketed (Today / Yesterday / …), most-recent first — same as the
  // normal chat sidebar. Only non-empty buckets are rendered.
  const groups = useMemo(() => {
    if (!groupedData) return [] as Array<{ key: keyof GroupedChatData; items: ChatHistoryApiItem[] }>;
    return GROUP_ORDER.map((key) => ({
      key,
      items: groupedData[key].filter((c) => idSet.has(c.id)),
    })).filter((g) => g.items.length > 0);
  }, [groupedData, idSet]);
  const total = useMemo(() => groups.reduce((n, g) => n + g.items.length, 0), [groups]);

  const isActive = (chatId: string) => location.pathname.includes(`/chat/${chatId}`);

  return (
    <SidebarGroup className="flex-1 pt-1">
      <SidebarGroupContent>
        {isLoading && total === 0 ? (
          <SidebarMenu>
            {Array.from({ length: 3 }).map((_, i) => (
              <SidebarMenuItem key={`cowork-task-skel-${i}`}>
                <SidebarMenuSkeleton showIcon />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        ) : total === 0 ? (
          <p className="px-3 py-1.5 text-xs leading-relaxed text-muted-foreground/70">
            No tasks yet. Ask the agent to work your folder and it’ll show up here.
          </p>
        ) : (
          <div className="space-y-2">
            {groups.map((group) => (
              <div key={group.key}>
                <div className="type-label px-3 pb-1 pt-1 text-muted-foreground/70">{GROUP_LABEL[group.key]}</div>
                <SidebarMenu>
                  {group.items.map((chat) => (
                    <SidebarMenuItem key={chat.id}>
                      <SidebarMenuButton isActive={isActive(chat.id)} asChild>
                        <Link to={chatPath(chat, appId)} className="flex min-w-0 items-center gap-3">
                          <DynamicIcon
                            name={resolveChatIcon(chat.metadata?.icon ?? null) as Parameters<typeof DynamicIcon>[0]["name"]}
                            className="size-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                          <span className="truncate">{chat.title || "New task"}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </div>
            ))}
          </div>
        )}
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
